// Manage a team's composition: its members and the roles granted to the whole
// team. Two panels sharing the same shape as the user-detail screen — what the
// team HAS is a row of removable chips, what it COULD have is a searchable
// picker that adds on one click. Every mutation reconciles server-side (each
// member's effective scopes change), so the drawer just reloads the team after.

import { useMemo, useState } from "react"
import { adminApi } from "../api/endpoints.js"
import type { RoleDto, TeamRoleSummaryDto, UserDto } from "../api/types.js"
import { useWritesEnabled } from "../auth/AdminAuthContext.js"
import { fr } from "../i18n/fr.js"
import { messageForError } from "../lib/errors.js"
import { useAsync } from "../lib/useAsync.js"
import { userFullName } from "../lib/users.js"
import { Drawer } from "../ui/Drawer.js"
import { PlusIcon } from "../ui/icons.js"
import { Alert, Card, Chip, RemovableChip, Spinner } from "../ui/primitives.js"
import { useNotify } from "../ui/toast.js"
import { ShopAccessCard } from "./ShopAccessCard.js"

interface Props {
  teamId: string
  org: string
  onClose: () => void
  onSaved: () => void
}

function roleLabel(role: { displayName: string; name: string }): string {
  return role.displayName || role.name
}

export function TeamMembersDrawer({ teamId, org, onClose, onSaved }: Props) {
  const notify = useNotify()
  const writesEnabled = useWritesEnabled()

  const team = useAsync(() => adminApi.getTeam(teamId), [teamId])
  const orgUsers = useAsync<UserDto[]>(() => adminApi.listUsers(org), [org])
  const orgRoles = useAsync<RoleDto[]>(() => adminApi.listRoles(org), [org])

  const [busy, setBusy] = useState(false)
  const [memberQuery, setMemberQuery] = useState("")
  const [roleQuery, setRoleQuery] = useState("")

  const memberIds = useMemo(() => new Set((team.data?.members ?? []).map((m) => m.id)), [team.data])
  const roleIds = useMemo(() => new Set((team.data?.roles ?? []).map((r) => r.id)), [team.data])

  const availableUsers = useMemo(
    () => (orgUsers.data ?? []).filter((u) => !memberIds.has(u.id)),
    [orgUsers.data, memberIds]
  )
  const availableRoles = useMemo(
    () => (orgRoles.data ?? []).filter((r) => !roleIds.has(r.id)),
    [orgRoles.data, roleIds]
  )

  const memberMatches = useMemo(() => {
    const q = memberQuery.trim().toLowerCase()
    if (!q) return availableUsers
    return availableUsers.filter(
      (u) => userFullName(u).toLowerCase().includes(q) || u.email.toLowerCase().includes(q)
    )
  }, [availableUsers, memberQuery])

  const roleMatches = useMemo(() => {
    const q = roleQuery.trim().toLowerCase()
    if (!q) return availableRoles
    return availableRoles.filter(
      (r) => roleLabel(r).toLowerCase().includes(q) || r.name.toLowerCase().includes(q)
    )
  }, [availableRoles, roleQuery])

  async function mutate(action: () => Promise<unknown>, failMsg: string, successMsg: string) {
    if (!writesEnabled) return
    setBusy(true)
    try {
      await action()
      notify.success(successMsg)
      team.reload()
      onSaved()
    } catch (err) {
      notify.error(messageForError(err, failMsg))
    } finally {
      setBusy(false)
    }
  }

  function addMember(user: UserDto) {
    void mutate(
      () => adminApi.addTeamMember(teamId, user.id),
      fr.teams.manage.loadFailed,
      fr.teams.manage.addMemberSuccess(userFullName(user))
    )
  }

  function removeMember(user: UserDto) {
    void mutate(
      () => adminApi.removeTeamMember(teamId, user.id),
      fr.teams.manage.loadFailed,
      fr.teams.manage.removeMemberSuccess(userFullName(user))
    )
  }

  function assignRole(role: RoleDto) {
    void mutate(
      () => adminApi.assignTeamRole(teamId, role.id),
      fr.teams.manage.loadFailed,
      fr.teams.manage.assignRoleSuccess(roleLabel(role))
    )
  }

  function unassignRole(role: TeamRoleSummaryDto) {
    void mutate(
      () => adminApi.unassignTeamRole(teamId, role.id),
      fr.teams.manage.loadFailed,
      fr.teams.manage.removeRoleSuccess(roleLabel(role))
    )
  }

  return (
    <Drawer open title={fr.teams.manage.drawerTitle} subtitle={team.data?.name} onClose={onClose}>
      {team.loading ? <Spinner label={fr.teams.manage.loading} /> : null}
      {team.error ? <Alert variant="error">{fr.teams.manage.loadFailed}</Alert> : null}

      {team.data ? (
        <div className="stack">
          {/* Members ─ what the team HAS + a picker of who it could add. */}
          <Card>
            <div className="card__header">
              <strong>{fr.teams.manage.membersTitle}</strong>
              <span className="muted" style={{ fontSize: 13 }}>
                {fr.teams.memberCount(team.data.members.length)}
              </span>
            </div>
            {team.data.members.length === 0 ? (
              <p className="muted" style={{ margin: 0 }}>
                {fr.teams.manage.membersEmpty}
              </p>
            ) : (
              <div className="row" style={{ gap: 6 }}>
                {team.data.members.map((member) => (
                  <RemovableChip
                    key={member.id}
                    title={member.email}
                    removeLabel={fr.teams.manage.removeMemberLabel(userFullName(member))}
                    disabled={!writesEnabled || busy}
                    onRemove={() => removeMember(member)}
                  >
                    {userFullName(member)}
                  </RemovableChip>
                ))}
              </div>
            )}
            <div style={{ marginTop: 12 }}>
              {availableUsers.length === 0 ? (
                <p className="muted" style={{ margin: 0 }}>
                  {fr.teams.manage.allMembers}
                </p>
              ) : (
                <div className="role-picker">
                  <input
                    type="search"
                    className="field__input"
                    aria-label={fr.teams.manage.memberSearchLabel}
                    placeholder={fr.teams.manage.memberSearchPlaceholder}
                    value={memberQuery}
                    onChange={(e) => setMemberQuery(e.target.value)}
                  />
                  {memberMatches.length === 0 ? (
                    <p className="muted" style={{ margin: 0 }}>
                      {fr.teams.manage.noMemberMatch}
                    </p>
                  ) : (
                    <div className="role-picker__list">
                      {memberMatches.map((user) => (
                        <button
                          key={user.id}
                          type="button"
                          className="role-option"
                          aria-label={fr.teams.manage.addMemberLabel(userFullName(user))}
                          disabled={!writesEnabled || busy}
                          onClick={() => addMember(user)}
                        >
                          <span className="role-option__main">
                            <span className="role-option__name">{userFullName(user)}</span>
                            <span className="mono subtitle">{user.email}</span>
                          </span>
                          <span className="role-option__add">
                            <PlusIcon />
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </Card>

          {/* Roles ─ what the team grants + a picker of the roles it could add. */}
          <Card>
            <div className="card__header">
              <strong>{fr.teams.manage.rolesTitle}</strong>
              <span className="muted" style={{ fontSize: 13 }}>
                {fr.teams.manage.rolesHint}
              </span>
            </div>
            {team.data.roles.length === 0 ? (
              <p className="muted" style={{ margin: 0 }}>
                {fr.teams.manage.rolesEmpty}
              </p>
            ) : (
              <div className="row" style={{ gap: 6 }}>
                {team.data.roles.map((role) => (
                  <RemovableChip
                    key={role.id}
                    system={role.isSystem}
                    title={role.name}
                    removeLabel={fr.teams.manage.removeRoleLabel(roleLabel(role))}
                    disabled={!writesEnabled || busy}
                    onRemove={() => unassignRole(role)}
                  >
                    {roleLabel(role)}
                  </RemovableChip>
                ))}
              </div>
            )}
            <div style={{ marginTop: 12 }}>
              {availableRoles.length === 0 ? (
                <p className="muted" style={{ margin: 0 }}>
                  {fr.teams.manage.allRoles}
                </p>
              ) : (
                <div className="role-picker">
                  <input
                    type="search"
                    className="field__input"
                    aria-label={fr.teams.manage.roleSearchLabel}
                    placeholder={fr.teams.manage.roleSearchPlaceholder}
                    value={roleQuery}
                    onChange={(e) => setRoleQuery(e.target.value)}
                  />
                  {roleMatches.length === 0 ? (
                    <p className="muted" style={{ margin: 0 }}>
                      {fr.teams.manage.noRoleMatch}
                    </p>
                  ) : (
                    <div className="role-picker__list">
                      {roleMatches.map((role) => (
                        <button
                          key={role.id}
                          type="button"
                          className="role-option"
                          aria-label={fr.teams.manage.assignRoleLabel(roleLabel(role))}
                          disabled={!writesEnabled || busy}
                          onClick={() => assignRole(role)}
                        >
                          <span className="role-option__main">
                            <span className="role-option__name">{roleLabel(role)}</span>
                            <span className="mono subtitle">{role.name}</span>
                          </span>
                          {role.isSystem ? <Chip system>{fr.roles.systemBadge}</Chip> : null}
                          <span className="role-option__add">
                            <PlusIcon />
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </Card>

          {/* Shops assigned to the whole team (inherited by every member). */}
          <ShopAccessCard
            org={org}
            roleIds={team.data.roles.map((r) => r.id)}
            hint={fr.shops.teamHint}
            reloadKey={teamId}
            loadShops={() => adminApi.listTeamShops(teamId)}
            saveShops={(shopIds) => adminApi.setTeamShops(teamId, shopIds)}
          />
        </div>
      ) : null}
    </Drawer>
  )
}
